import { IDetectionService, DetectionBox, SideAlignment, ClassificationResult } from './interfaces/IDetectionService';
import { IDeviceStrategy } from './strategies/IDeviceStrategy';
import { DeviceStrategyFactory } from './factories/DeviceStrategyFactory';

export class DetectionService implements IDetectionService {
  private session?: any;
  private mobileNetSession?: any;
  private mobileNetClassMap?: any;
  private modelLoaded: boolean = false;
  private deviceStrategy: IDeviceStrategy;

  private readonly MODEL_PATH = "https://static.jaak.ai/web/component/stamps/ddmyp-v2.onnx";
  private readonly MODEL_PATH_FP32 = "https://static.jaak.ai/web/component/stamps/ddmyp-v2-fp32.onnx";
  private readonly MOBILENET_MODEL_PATH = "https://static.jaak.ai/web/component/stamps/squeezenet_new_fp32.onnx";
  private readonly MOBILENET_CLASSES_PATH = "https://static.jaak.ai/web/component/stamps/cdmmp-v1.json";
  private readonly INPUT_SIZE = 320;
  private readonly CONFIDENCE_THRESHOLD = 0.6;
  private useFloat16: boolean = true;

  // Hoisted views for float32->float16 bit-twiddling. A fresh ArrayBuffer
  // per call was costing ~614K allocations per frame.
  private readonly _f32View = new Float32Array(1);
  private readonly _u32View = new Uint32Array(this._f32View.buffer);

  // Pre-allocated tensor buffers reused across frames. Same shape every time
  // (3 * INPUT_SIZE * INPUT_SIZE), and runInference is awaited before the next
  // preprocess runs, so reuse is safe.
  private readonly _tensorF32 = new Float32Array(3 * 320 * 320);
  private readonly _tensorF16 = new Uint16Array(3 * 320 * 320);

  // Canvas pool for optimization
  private preprocessCanvas?: HTMLCanvasElement;
  private preprocessCtx?: CanvasRenderingContext2D;
  private captureCanvas?: HTMLCanvasElement;
  
  // Static canvas pool for reuse across instances
  private static canvasPool = new Map<string, HTMLCanvasElement[]>();
  private static readonly MAX_POOL_SIZE = 5;

  constructor(
    private debug: boolean = false,
    private useDocumentClassification: boolean = false,
    private useDocumentDetector: boolean = true
  ) {
    this.deviceStrategy = DeviceStrategyFactory.createStrategy();
    this.initializeCanvasPool();
  }

  async loadModel(): Promise<void> {
    if (this.modelLoaded || this.session) {
      return;
    }

    if (!this.useDocumentDetector) {
      // Skip loading the detection model but mark as "loaded" for compatibility
      this.modelLoaded = true;
      return;
    }

    try {

      const sessionOptions = this.deviceStrategy.getSessionOptions(this.debug);

      try {
        this.session = await (window as any).ort.InferenceSession.create(this.MODEL_PATH, sessionOptions);
        this.detectFloat16Support();
      } catch (error) {
        const errorMsg = error.message || '';
        const isMemoryError = errorMsg.includes('failed to allocate a buffer') ||
                             errorMsg.includes('Out of memory') ||
                             errorMsg.includes('RangeError: Out of memory') ||
                             errorMsg.includes('no available backend found');
        const isFloat16Error = errorMsg.includes('unsupported data type') ||
                               errorMsg.includes('FLOAT16') ||
                               errorMsg.includes('float16');

        if (isFloat16Error) {
          this.useFloat16 = false;
          try {
            this.session = await (window as any).ort.InferenceSession.create(this.MODEL_PATH_FP32, sessionOptions);
          } catch (fp32Error) {
            // Intentar con opciones minimas de WASM
            const wasmOptions = {
              executionProviders: ['wasm'],
              graphOptimizationLevel: 'all',
              logSeverityLevel: this.debug ? 2 : 4,
            };
            this.session = await (window as any).ort.InferenceSession.create(this.MODEL_PATH_FP32, wasmOptions);
          }
        } else if (isMemoryError) {
          const fallbackOptions = {
            executionProviders: ['wasm'],
            graphOptimizationLevel: 'disabled',
            logSeverityLevel: 4,
            enableCpuMemArena: false,
            enableMemPattern: false,
            executionMode: 'sequential',
            interOpNumThreads: 1,
            intraOpNumThreads: 1,
            enableProfiling: false,
            sessionLogSeverityLevel: 4,
            sessionLogVerbosityLevel: 0,
          };

          this.useFloat16 = false;

          try {
            this.session = await (window as any).ort.InferenceSession.create(this.MODEL_PATH_FP32, fallbackOptions);
          } catch (fallbackError) {
            throw new Error(`no available backend found. ERR: [wasm] RangeError: Out of memory`);
          }
        } else {
          throw error;
        }
      }

      this.modelLoaded = true;
    } catch (error) {
      throw error;
    }
  }

  async loadClassificationModel(): Promise<void> {
    if (!this.useDocumentClassification) {
      return;
    }

    try {

      // Try to load class map (optional - SqueezeNet may not need it for basic classification)
      try {
        const classResponse = await fetch(this.MOBILENET_CLASSES_PATH);
        if (classResponse.ok) {
          this.mobileNetClassMap = await classResponse.json();
        }
      } catch (error) {
        // Create a basic class map for document types
        this.mobileNetClassMap = {
          "0": "document",
          "1": "id_card",
          "2": "passport",
          "3": "license",
          "4": "other"
        };
      }

      // Load model
      const sessionOptions = this.deviceStrategy.getSessionOptions(this.debug);
      try {
        this.mobileNetSession = await (window as any).ort.InferenceSession.create(this.MOBILENET_MODEL_PATH, sessionOptions);
      } catch (error) {
        // Múltiples tipos de errores de memoria que pueden ocurrir
        const isMemoryError = error.message.includes('failed to allocate a buffer') ||
                             error.message.includes('Out of memory') ||
                             error.message.includes('RangeError: Out of memory') ||
                             error.message.includes('no available backend found');
        
        if (isMemoryError) {
          const fallbackOptions = {
            executionProviders: ['wasm'],
            graphOptimizationLevel: 'disabled',
            logSeverityLevel: 4,
            enableCpuMemArena: false,
            enableMemPattern: false,
            executionMode: 'sequential',
            interOpNumThreads: 1,
            intraOpNumThreads: 1,
            // Configuraciones adicionales para dispositivos con poca memoria
            enableProfiling: false,
            sessionLogSeverityLevel: 4,
            sessionLogVerbosityLevel: 0,
          };
          
          try {
            this.mobileNetSession = await (window as any).ort.InferenceSession.create(this.MOBILENET_MODEL_PATH, fallbackOptions);
          } catch (fallbackError) {
            throw new Error(`no available backend found. ERR: [wasm] RangeError: Out of memory`);
          }
        } else {
          throw error;
        }
      }

    } catch (error) {
      throw error;
    }
  }

  isModelLoaded(): boolean {
    if (!this.useDocumentDetector) {
      // If detector is disabled, consider it "loaded" for compatibility
      return this.modelLoaded;
    }
    return this.modelLoaded && !!this.session;
  }

  preprocess(video: HTMLVideoElement): any {
    if (!this.preprocessCanvas || !this.preprocessCtx) {
      this.initializeCanvasPool();
    }

    const SIZE = this.INPUT_SIZE;
    this.preprocessCtx!.clearRect(0, 0, SIZE, SIZE);
    this.preprocessCtx!.drawImage(video, 0, 0, SIZE, SIZE);

    const imageData = this.preprocessCtx!.getImageData(0, 0, SIZE, SIZE);
    const { data } = imageData;
    const pixels = SIZE * SIZE;
    const gOffset = pixels;
    const bOffset = pixels * 2;
    const inv255 = 1 / 255;

    if (this.useFloat16) {
      const out = this._tensorF16;
      for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        out[p]           = this.float32ToFloat16(data[i]     * inv255);
        out[gOffset + p] = this.float32ToFloat16(data[i + 1] * inv255);
        out[bOffset + p] = this.float32ToFloat16(data[i + 2] * inv255);
      }
      return new (window as any).ort.Tensor("float16", out, [1, 3, SIZE, SIZE]);
    }

    const out = this._tensorF32;
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      out[p]           = data[i]     * inv255;
      out[gOffset + p] = data[i + 1] * inv255;
      out[bOffset + p] = data[i + 2] * inv255;
    }
    return new (window as any).ort.Tensor("float32", out, [1, 3, SIZE, SIZE]);
  }

  async runInference(inputTensor: any): Promise<DetectionBox[]> {
    if (!this.useDocumentDetector) {
      // Return empty array when document detector is disabled
      return [];
    }

    if (!this.session) {
      throw new Error('Detection model not loaded');
    }

    const feeds = { [this.session.inputNames[0]]: inputTensor };
    const results = await this.session.run(feeds);
    const output = results[this.session.outputNames[0]].data;

    const detections: DetectionBox[] = [];
    for (let i = 0; i < output.length; i += 6) {
      const [x1, y1, x2, y2, score, classId] = output.slice(i, i + 6);
      if (score > this.CONFIDENCE_THRESHOLD) {
        detections.push({
          x: x1,
          y: y1,
          w: x2 - x1,
          h: y2 - y1,
          score,
          classId
        });
      }
    }

    return detections;
  }

  async classifyDocument(canvas: HTMLCanvasElement): Promise<ClassificationResult | null> {
    if (!this.mobileNetSession || !this.mobileNetClassMap) {
      return null;
    }

    try {

      const inputTensor = this.preprocessMobileNet(canvas);
      const feeds = { [this.mobileNetSession.inputNames[0]]: inputTensor };
      const results = await this.mobileNetSession.run(feeds);
      const output = results[Object.keys(results)[0]].data;

      const maxIdx = output.reduce((bestIdx: number, val: number, idx: number, arr: number[]) =>
        val > arr[bestIdx] ? idx : bestIdx, 0);
      const confidence = output[maxIdx];

      const className = this.mobileNetClassMap[maxIdx.toString()] || "unknown";


      return {
        class: className,
        confidence: confidence,
        classIndex: maxIdx
      };

    } catch (error) {
      return null;
    }
  }

  checkSideAlignment(box: DetectionBox, maskConfig: any): SideAlignment {
    const { INPUT_SIZE, ID1_ASPECT_RATIO, shouldMirrorVideo, alignmentTolerance, maskSize, videoRef } = maskConfig;

    if (!videoRef) {
      return { top: false, right: false, bottom: false, left: false };
    }

    // Get video dimensions to calculate actual mask boundaries in model space
    const videoWidth = videoRef.videoWidth;
    const videoHeight = videoRef.videoHeight;

    if (videoWidth === 0 || videoHeight === 0) {
      return { top: false, right: false, bottom: false, left: false };
    }

    // Calculate video aspect ratio
    const videoAspectRatio = videoWidth / videoHeight;

    // The model sees video stretched to 320x320, but we need to calculate where
    // the mask should be in this distorted space to match the visual mask

    // In the visual display, we calculate mask size based on the limiting dimension
    // We need to replicate this logic but account for the distortion in model space

    // Calculate the "display" dimensions (what the visual mask calculations use)
    const containerAspectRatio = 1; // Assume square container for simplicity
    let displayedVideoWidth, displayedVideoHeight;

    if (videoAspectRatio > containerAspectRatio) {
      // Video is wider - letterboxed in display
      displayedVideoWidth = INPUT_SIZE;
      displayedVideoHeight = INPUT_SIZE / videoAspectRatio;
    } else {
      // Video is taller - pillarboxed in display
      displayedVideoHeight = INPUT_SIZE;
      displayedVideoWidth = INPUT_SIZE * videoAspectRatio;
    }

    // Calculate mask dimensions using the same logic as updateMaskDimensions
    const maxWidthByHeight = displayedVideoHeight * ID1_ASPECT_RATIO;

    let visualMaskWidth, visualMaskHeight;
    const sizeRatio = maskSize / 100;

    if (maxWidthByHeight <= displayedVideoWidth) {
      // Video height is the limiting factor
      visualMaskHeight = displayedVideoHeight * sizeRatio;
      visualMaskWidth = visualMaskHeight * ID1_ASPECT_RATIO;
    } else {
      // Video width is the limiting factor
      visualMaskWidth = displayedVideoWidth * sizeRatio;
      visualMaskHeight = visualMaskWidth / ID1_ASPECT_RATIO;
    }

    // Now map these visual mask dimensions to the distorted model space
    // The model stretches video to 320x320, so we need to apply the inverse transformation
    const modelMaskWidth = visualMaskWidth * (INPUT_SIZE / displayedVideoWidth);
    const modelMaskHeight = visualMaskHeight * (INPUT_SIZE / displayedVideoHeight);

    // Calculate mask boundaries in model coordinate system (always centered in 320x320)
    const maskCenterX = INPUT_SIZE / 2;
    const maskCenterY = INPUT_SIZE / 2;
    const maskLeft = maskCenterX - (modelMaskWidth / 2);
    const maskRight = maskCenterX + (modelMaskWidth / 2);
    const maskTop = maskCenterY - (modelMaskHeight / 2);
    const maskBottom = maskCenterY + (modelMaskHeight / 2);

    // Obtener las coordenadas del documento detectado
    let docLeft = box.x;
    let docRight = box.x + box.w;
    const docTop = box.y;
    const docBottom = box.y + box.h;

    // Si el video está en modo mirror, invertir las coordenadas horizontales
    if (shouldMirrorVideo) {
      const originalDocLeft = docLeft;
      const originalDocRight = docRight;
      docLeft = INPUT_SIZE - originalDocRight;
      docRight = INPUT_SIZE - originalDocLeft;
    }

    // Tolerancia para considerar que un lado está alineado (en píxeles)
    const tolerance = alignmentTolerance;

    // Verificar alineación de esquinas más estricta
    // Una esquina está alineada solo si AMBOS bordes que la forman están alineados
    const topAligned = Math.abs(docTop - maskTop) <= tolerance;
    const rightAligned = Math.abs(docRight - maskRight) <= tolerance;
    const bottomAligned = Math.abs(docBottom - maskBottom) <= tolerance;
    const leftAligned = Math.abs(docLeft - maskLeft) <= tolerance;

    return {
      top: topAligned && leftAligned,     // Esquina superior izquierda: borde top Y left alineados
      right: topAligned && rightAligned,  // Esquina superior derecha: borde top Y right alineados
      bottom: bottomAligned && leftAligned, // Esquina inferior izquierda: borde bottom Y left alineados
      left: bottomAligned && rightAligned   // Esquina inferior derecha: borde bottom Y right alineados
    };
  }

  isCardInFrame(box: DetectionBox): boolean {
    const cardCenterX = box.x + box.w / 2;
    const cardCenterY = box.y + box.h / 2;
    const frameCenterX = this.INPUT_SIZE / 2;
    const frameCenterY = this.INPUT_SIZE / 2;
    const toleranceX = 40;
    const toleranceY = 30;

    const isCentered = Math.abs(cardCenterX - frameCenterX) < toleranceX &&
                       Math.abs(cardCenterY - frameCenterY) < toleranceY;
    const isGoodSize = box.w > 150 && box.w < 300 && box.h > 90 && box.h < 200;

    return isCentered && isGoodSize;
  }

  areAllSidesAligned(alignment: SideAlignment): boolean {
    return alignment.top && alignment.right && alignment.bottom && alignment.left;
  }

  cleanup(): void {
    this.cleanupCanvasPool();

    if (this.session) {
      this.session.release?.();
      this.session = undefined;
    }
    if (this.mobileNetSession) {
      this.mobileNetSession.release?.();
      this.mobileNetSession = undefined;
    }
    this.mobileNetClassMap = undefined;
    this.modelLoaded = false;

  }

  private initializeCanvasPool(): void {
    this.preprocessCanvas = document.createElement('canvas');
    this.preprocessCanvas.width = this.INPUT_SIZE;
    this.preprocessCanvas.height = this.INPUT_SIZE;
    this.preprocessCtx = this.preprocessCanvas.getContext('2d', {
      alpha: false,
      willReadFrequently: true
    })!;

    this.captureCanvas = document.createElement('canvas');

  }

  private cleanupCanvasPool(): void {
    if (this.preprocessCanvas) {
      this.preprocessCtx = undefined;
      this.preprocessCanvas = undefined;
    }
    if (this.captureCanvas) {
      this.captureCanvas = undefined;
    }
  }

  private detectFloat16Support(): void {
    try {
      // Verificar si el backend actual soporta float16 intentando crear un tensor pequeno
      const testData = new Uint16Array([0]);
      new (window as any).ort.Tensor("float16", testData, [1]);
      this.useFloat16 = true;
    } catch {
      this.useFloat16 = false;
    }
  }

  private float32ToFloat16(value: number): number {
    this._f32View[0] = value;
    const f = this._u32View[0];
    const sign = (f >> 31) & 0x1;
    const exp = (f >> 23) & 0xFF;
    const frac = f & 0x7FFFFF;

    let newExp = exp - 127 + 15;

    if (exp === 0) {
      newExp = 0;
    } else if (exp === 0xFF) {
      newExp = 0x1F;
    } else if (newExp >= 0x1F) {
      newExp = 0x1F;
      return (sign << 15) | (newExp << 10);
    } else if (newExp <= 0) {
      return (sign << 15);
    }

    return (sign << 15) | (newExp << 10) | (frac >> 13);
  }

  private preprocessMobileNet(canvas: HTMLCanvasElement): any {
    // Use pooled canvas instead of creating new one
    const tempCanvas = this.getCanvas(224, 224);
    const tempCtx = tempCanvas.getContext('2d')!;

    // Clear canvas before use
    tempCtx.clearRect(0, 0, 224, 224);
    
    // Resize image to 224x224 for SqueezeNet (using MobileNet interface)
    tempCtx.drawImage(canvas, 0, 0, 224, 224);

    const imageData = tempCtx.getImageData(0, 0, 224, 224);
    const data = imageData.data;
    const hw = 224 * 224;
    const arr = new Float32Array(3 * hw);

    // SqueezeNet preprocessing: normalize to [0,1] range and arrange in CHW format
    for (let i = 0; i < hw; i++) {
      // Red channel
      arr[i] = data[i * 4 + 0] / 255.0;
      // Green channel
      arr[hw + i] = data[i * 4 + 1] / 255.0;
      // Blue channel
      arr[2 * hw + i] = data[i * 4 + 2] / 255.0;
    }

    // Return canvas to pool after use
    this.returnCanvas(tempCanvas);

    return new (window as any).ort.Tensor('float32', arr, [1, 3, 224, 224]);
  }

  // Canvas pool management methods for optimization
  private getCanvas(width: number, height: number): HTMLCanvasElement {
    const key = `${width}x${height}`;
    if (!DetectionService.canvasPool.has(key)) {
      DetectionService.canvasPool.set(key, []);
    }
    const pool = DetectionService.canvasPool.get(key)!;
    
    let canvas = pool.pop();
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
    } else {
      // Clear the canvas before reuse
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, width, height);
    }
    
    return canvas;
  }

  private returnCanvas(canvas: HTMLCanvasElement): void {
    const key = `${canvas.width}x${canvas.height}`;
    if (!DetectionService.canvasPool.has(key)) {
      DetectionService.canvasPool.set(key, []);
    }
    const pool = DetectionService.canvasPool.get(key)!;
    
    // Only return to pool if not at max capacity
    if (pool.length < DetectionService.MAX_POOL_SIZE) {
      pool.push(canvas);
    }
  }

  // Static method to clear all canvas pools (useful for memory management)
  public static clearCanvasPools(): void {
    DetectionService.canvasPool.clear();
  }

  // Method to get pool statistics for debugging
  public getPoolStats(): { [key: string]: number } {
    const stats: { [key: string]: number } = {};
    DetectionService.canvasPool.forEach((pool, key) => {
      stats[key] = pool.length;
    });
    return stats;
  }

}
